import { Alert, Box, Button, Card, CardContent, Chip, FormControlLabel, Grid, Paper, Slider, Switch, TextField, Typography } from '@mui/material';
import React from 'react';

interface GeneralGroupingsProps {
  weightClasses: Array<{ name: string; wrestlers: any[]; minWeight: number; maxWeight: number; gender?: string; ageGroup?: string }>;
  weightDelta: number;
  ageDelta: number;
  separateGenders: boolean;
  showAdvanced: boolean;
  currentStep: number;
  drawComplete: boolean;
  onWeightDeltaChange: (value: number) => void;
  onAgeDeltaChange: (value: number) => void;
  onSeparateGendersChange: (value: boolean) => void;
  onShowAdvancedChange: (value: boolean) => void;
  onContinueClick: () => void;
}

const GeneralGroupings: React.FC<GeneralGroupingsProps> = ({
  weightClasses,
  weightDelta,
  ageDelta,
  separateGenders,
  showAdvanced,
  currentStep,
  drawComplete,
  onWeightDeltaChange,
  onAgeDeltaChange,
  onSeparateGendersChange,
  onShowAdvancedChange,
  onContinueClick
}) => {
  return (
        <>
          <Box>
            <Typography variant="h6" gutterBottom>
              Step 1: Basic Groupings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Review the automatically generated weight class groupings. If you are happy with the groupings based on the Weight Class Settings you provided, click "Adjust Groupings" to proceed to make finer adjustments. Otherwise, go back and adjust the settings.
            </Typography>
          </Box>

          {/* Weight Class Settings */}
          <Paper sx={{ p: 3, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                General Settings
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={showAdvanced}
                    onChange={(e) => onShowAdvancedChange(e.target.checked)}
                  />
                }
                label="Advanced"
              />
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Weight Delta: {weightDelta.toFixed(1)} kg
                </Typography>
                <Slider
                  value={weightDelta}
                  onChange={(_, newValue) => onWeightDeltaChange(newValue as number)}
                  min={1}
                  max={10}
                  step={0.5}
                  marks={[
                    { value: 1, label: '1' },
                    { value: 2.5, label: '2.5' },
                    { value: 5, label: '5' },
                    { value: 7.5, label: '7.5' },
                    { value: 10, label: '10' }
                  ]}
                  valueLabelDisplay="auto"
                  sx={{ mb: 2 }}
                />
                <Typography variant="caption" color="text.secondary">
                  Minimum weight difference between weight classes. Smaller deltas create more specific weight classes.
                </Typography>
                <p></p>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Age Delta: {ageDelta} years
                </Typography>

                <Slider
                  value={ageDelta}
                  onChange={(_, newValue) => onAgeDeltaChange(newValue as number)}
                  min={1}
                  max={10}
                  step={1}
                  marks={[
                    { value: 1, label: '1' },
                    { value: 2, label: '2' },
                    { value: 3, label: '3' },
                    { value: 4, label: '4' },
                    { value: 5, label: '5' },
                    { value: 6, label: '6' },
                    { value: 7, label: '7' },
                    { value: 8, label: '8' },
                    { value: 9, label: '9' },
                    { value: 10, label: '10' }
                  ]}
                  valueLabelDisplay="auto"
                  sx={{ mb: 2 }}
                />
                <Typography variant="caption" color="text.secondary">
                  Age grouping for subdividing weight classes by grade/age. Each weight class will be separated by grade (e.g., Grade 4, Grade 5, etc.).
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={separateGenders}
                      onChange={(e) => onSeparateGendersChange(e.target.checked)}
                    />
                  }
                  label="Separate by Gender"
                  sx={{ mb: 2 }}
                />
                <br></br>
                <Typography variant="caption" color="text.secondary">
                  {separateGenders 
                    ? "Create separate weight classes for boys and girls"
                    : "Create mixed gender weight classes"
                  }
                </Typography>
              </Grid>
              
              {showAdvanced && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Custom Weight Delta"
                    type="number"
                    value={weightDelta}
                    onChange={(e) => onWeightDeltaChange(parseFloat(e.target.value) || 2.5)}
                    inputProps={{ min: 0.5, max: 20, step: 0.1 }}
                    helperText="Enter custom delta (0.5 - 20 kg)"
                    fullWidth
                  />
                </Grid>
              )}
            </Grid>

            <Alert severity="info" sx={{ mt: 2 }}>
              Current delta of {weightDelta.toFixed(1)}kg creates {weightClasses.length} weight classes
              {weightClasses.length > 10 && ' (consider using a larger delta)'}
              {weightClasses.length < 3 && ' (consider using a smaller delta)'}
            </Alert>
          </Paper>

          {/* Weight Classes Summary*/}
          {!drawComplete && (currentStep === 0) && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Initial Groupings
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Below find the initial groupings based on your general settings. In the next step, you can further customize these groupings.
              </Typography>
              <Grid container spacing={2}>
                {weightClasses.map((weightClass) => (
                  <Grid item xs={12} sm={6} md={4} key={weightClass.name}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom>
                          {weightClass.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {weightClass.wrestlers.length} wrestlers
                        </Typography>
                        <Box sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
                          {weightClass.wrestlers.map((w, i) => (
                            <Chip
                              key={i}
                              label={`${w.firstName} ${w.lastName} (${w.weightKg.toFixed(1)}kg)`}
                              size="small"
                              variant="outlined"
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))}
                        </Box>
                      </CardContent>
                    </Card>

                    
                  </Grid>



                ))}
              </Grid>
              <br></br>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Button
                  variant="contained"
                  onClick={onContinueClick}
                >
                  Continue to Step 2: Adjust Groupings
                </Button>
              </Box>

            </Paper>
          )}
        </>
  )
}

export default GeneralGroupings;
